/**
 * ZAHL-02c (Betreiberfreigabe 13.10., K5/Soll 4) – Ops-Center · Abschnitt „Vertrag" je Mandant.
 *
 * Eigener Abschnitt neben „Abrechnung": Abrechnung ist rein lesend, hier gibt es Schreibaktionen
 * (Kündigung eintragen / zurücknehmen) mit Pflichtfeldern, Vorschau des Wirksamkeitsdatums und
 * Bestätigungsschritt. Daten TT.MM.JJJJ aus dem Backend (Europe/Berlin). Kein Stripe im Frontend.
 * Außerhalb abo_pro_mitarbeiter: nur Hinweis, keine Aktionen (K5/V3).
 */
import { useCallback, useEffect, useState } from "react";
import { FileSignature, Loader2 } from "lucide-react";
import { opsApi, opsErrorMessage } from "@/lib/opsApi";

const INITIATOR_LABEL = { kunde: "Kunde", clartext: "CLARTEXT" };
// ZAHL-02d (Betreiberfreigabe 14.10., Soll 8): Zustand nach Vertragsende.
const ZUSTAND_LABEL = { lesephase: "Lesephase", gesperrt: "gesperrt" };

function fmtTagIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
}

// ZAHL-02d (Soll 8): Vertragsende, Ende Lesephase, Zustand; Endabrechnung je Position (Status) als Kurzzeile.
// ZAHL-02e-2 (Betreiberfreigabe 14.10., S4, Soll 4): Stand kommt aus billing_trueups (abgebrochen = fehlgeschlagen mit
// Fehlertext); läuft eine Position noch (in_arbeit unter 15 Minuten), steht „Endabrechnung wird erstellt.".
export const TEXT_ENDABRECHNUNG_IN_ARBEIT = "Endabrechnung wird erstellt.";
function VertragsendeBlock({ ve }) {
  const end = ve.endabrechnung || [];
  const endText = end.some((e) => e && e.status === "in_arbeit")
    ? TEXT_ENDABRECHNUNG_IN_ARBEIT
    : end.length
      ? end.map((e) => `${e.position_id || "?"}: ${e.status}${e.units ? ` (Menge ${e.units})` : ""}${e.grund ? ` – ${e.grund}` : ""}`).join("; ")
      : "noch keine";
  return (
    <div className="space-y-1.5 rounded-md border border-red-200 bg-red-50/60 p-3" data-testid="ops-vertragsende">
      <p className="text-xs font-semibold uppercase tracking-wide text-red-800">Vertrag beendet</p>
      <Zeile label="Vertragsende" value={ve.vertragsende_text} testId="ops-vertragsende-datum" />
      <Zeile label="Ende Lesephase" value={ve.lesephase_bis_text} testId="ops-vertragsende-lesephase-bis" />
      <Zeile label="Zustand" value={ZUSTAND_LABEL[ve.zustand] || ve.zustand} testId="ops-vertragsende-zustand" />
      <Zeile label="Kündigung" value={ve.ohne_kuendigung ? "keine eingetragen" : "vollzogen"} testId="ops-vertragsende-kuendigung" />
      <Zeile label="Vertragsende-Mail" value={ve.mail?.status || "–"} testId="ops-vertragsende-mail" />
      <Zeile label="Endabrechnung" value={endText} testId="ops-vertragsende-endabrechnung" />
    </div>
  );
}

// ZAHL-02d (Soll 7/8): frühere (abgeschlossene) Verträge – nach Neuabschluss als Historie lesbar.
function VertragHistorie({ eintraege }) {
  const fruehere = (eintraege || []).filter((e) => !e.aktiv);
  if (!fruehere.length) return null;
  return (
    <div className="text-xs text-slate-500 space-y-0.5" data-testid="ops-vertrag-historie-vertraege">
      <p className="font-semibold uppercase tracking-wide">Frühere Verträge</p>
      {fruehere.map((e) => (
        <p key={e.stripe_subscription_id} data-testid="ops-vertrag-historie-vertrag">
          Vertrag {e.vertragsbeginn_text || "–"} bis {e.vertragsende_text || "–"} (Lesephase bis {e.lesephase_bis_text || "–"}), Neuabschluss am{" "}
          {fmtTagIso(e.neuabschluss_at) || "–"}
          {e.neue_subscription_id ? ` (${e.neue_subscription_id})` : ""}
        </p>
      ))}
    </div>
  );
}

function Zeile({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm" data-testid={testId}>
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value ?? "–"}</span>
    </div>
  );
}

function KuendigungFormular({ tenantId, onDone }) {
  const [eingangAm, setEingangAm] = useState("");
  const [initiator, setInitiator] = useState("kunde");
  const [begruendung, setBegruendung] = useState("");
  const [vorschau, setVorschau] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pflichtOk = begruendung.trim().length > 0;

  const ladeVorschau = async () => {
    setError("");
    setBusy(true);
    try {
      setVorschau(await opsApi.kuendigungVorschau(tenantId, eingangAm || undefined));
    } catch (e) {
      setVorschau(null);
      setError(opsErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const eintragen = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await opsApi.kuendigungEintragen(tenantId, {
        eingang_am: eingangAm || null,
        initiator,
        begruendung: begruendung.trim(),
      });
      setVorschau(null);
      onDone(res);
    } catch (e) {
      setError(opsErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="ops-kuendigung-formular">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Kündigung eintragen</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-slate-600 space-y-1">
          <span>Eingangsdatum (Standard: heute)</span>
          <input
            type="date"
            value={eingangAm}
            onChange={(e) => { setEingangAm(e.target.value); setVorschau(null); }}
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
            data-testid="ops-kuendigung-eingang"
          />
        </label>
        <label className="text-xs text-slate-600 space-y-1">
          <span>Initiator</span>
          <select
            value={initiator}
            onChange={(e) => setInitiator(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
            data-testid="ops-kuendigung-initiator"
          >
            <option value="kunde">Kunde (per E-Mail an info@clartext.io)</option>
            <option value="clartext">CLARTEXT</option>
          </select>
        </label>
      </div>
      <label className="block text-xs text-slate-600 space-y-1">
        <span>Begründung (Pflicht)</span>
        <textarea
          value={begruendung}
          onChange={(e) => setBegruendung(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
          data-testid="ops-kuendigung-begruendung"
        />
      </label>
      {!vorschau ? (
        <button
          type="button"
          onClick={ladeVorschau}
          disabled={busy || !pflichtOk}
          className="inline-flex items-center rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
          data-testid="ops-kuendigung-vorschau-btn"
        >
          Wirksamkeitsdatum berechnen
        </button>
      ) : (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="ops-kuendigung-bestaetigung">
          <p className="text-sm text-amber-900">
            Kündigung durch <strong>{INITIATOR_LABEL[initiator]}</strong>, Eingang am{" "}
            <strong data-testid="ops-kuendigung-vorschau-eingang">{vorschau.eingang_am_text}</strong>, wirksam zum{" "}
            <strong data-testid="ops-kuendigung-vorschau-wirksam">{vorschau.wirksam_zum_text}</strong>
            {vorschau.naechste_periode ? (
              <span data-testid="ops-kuendigung-vorschau-naechste">
                {" "}(Eingang nach dem letzten Kündigungstag {vorschau.letzter_kuendigungstag_massgeblich_text} → nächstes Laufzeitende).
              </span>
            ) : (
              "."
            )}
          </p>
          <p className="text-xs text-amber-900">
            {/* ZAHL-02d (Betreiberfreigabe 14.10., D1): Abo-Ende exakt auf der Stripe-Periodengrenze am Laufzeitende. */}
            In Stripe wird das Abo-Ende auf die Periodengrenze am Laufzeitende gesetzt
            {vorschau.cancel_at_text ? ` (${vorschau.cancel_at_text})` : ""}; die letzte Monatsrechnung bleibt ein voller Monat, keine anteilige Gutschrift. Der Owner erhält eine Bestätigungsmail.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={eintragen}
              disabled={busy}
              className="inline-flex items-center rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50 transition-colors"
              data-testid="ops-kuendigung-bestaetigen-btn"
            >
              Kündigung jetzt eintragen
            </button>
            <button
              type="button"
              onClick={() => setVorschau(null)}
              disabled={busy}
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="ops-kuendigung-abbrechen-btn"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="text-sm text-red-700" role="alert" data-testid="ops-kuendigung-error">
          {error}
        </p>
      )}
    </div>
  );
}

function Ruecknahme({ tenantId, onDone }) {
  const [offen, setOffen] = useState(false);
  const [begruendung, setBegruendung] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const absenden = async () => {
    setError("");
    setBusy(true);
    try {
      onDone(await opsApi.kuendigungZuruecknehmen(tenantId, begruendung.trim()));
      setOffen(false);
    } catch (e) {
      setError(opsErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
        data-testid="ops-kuendigung-ruecknahme-btn"
      >
        Kündigung zurücknehmen
      </button>
    );
  }
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="ops-kuendigung-ruecknahme-formular">
      <label className="block text-xs text-slate-600 space-y-1">
        <span>Begründung der Rücknahme (Pflicht)</span>
        <textarea
          value={begruendung}
          onChange={(e) => setBegruendung(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
          data-testid="ops-kuendigung-ruecknahme-begruendung"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={absenden}
          disabled={busy || !begruendung.trim()}
          className="inline-flex items-center rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
          data-testid="ops-kuendigung-ruecknahme-bestaetigen-btn"
        >
          Rücknahme eintragen
        </button>
        <button
          type="button"
          onClick={() => setOffen(false)}
          className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
          data-testid="ops-kuendigung-ruecknahme-abbrechen-btn"
        >
          Abbrechen
        </button>
      </div>
      {error && (
        <p className="text-sm text-red-700" role="alert" data-testid="ops-kuendigung-ruecknahme-error">
          {error}
        </p>
      )}
    </div>
  );
}

export default function OpsVertragSection({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [meldung, setMeldung] = useState("");

  const laden = useCallback(() => {
    if (!tenantId || typeof opsApi.tenantVertrag !== "function") return;
    setLoading(true);
    setError("");
    opsApi
      .tenantVertrag(tenantId)
      .then((d) => setData(d))
      .catch((e) => setError(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    laden();
  }, [laden]);

  const nachAktion = (text) => {
    setMeldung(text);
    laden();
  };

  const k = data?.kuendigung || null;
  // ZAHL-02d (Betreiberfreigabe 14.10., Soll 8): aktives Vertragsende (Lesephase / gesperrt) – kein Kündigungsformular mehr.
  const ve = data?.vertragsende || null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ops-detail-vertrag">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <FileSignature className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Vertrag</h3>
        {ve ? (
          <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800" data-testid="ops-vertragsende-badge">
            {ve.zustand === "gesperrt" ? "Vertrag beendet – gesperrt" : "Vertrag beendet – Lesephase"}
          </span>
        ) : k && (
          <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800" data-testid="ops-vertrag-status-badge">
            Gekündigt zum {k.wirksam_zum_text}
          </span>
        )}
      </div>
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="py-4 flex justify-center" data-testid="ops-vertrag-loading">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600" data-testid="ops-vertrag-error">{error}</p>
        ) : !data ? null : !data.geltungsbereich ? (
          <p className="text-sm text-slate-500 italic" data-testid="ops-vertrag-ausserhalb">
            Für diesen Vertrag ist die Kündigung über das Ops-Center nicht vorgesehen (kein Abo pro Mitarbeiter).
          </p>
        ) : (
          <>
            <div className="space-y-1.5" data-testid="ops-vertrag-daten">
              <Zeile label="Vertragsbeginn" value={data.vertragsbeginn_text} testId="ops-vertrag-beginn" />
              {!ve && (
                <>
                  <Zeile label="Laufzeit bis" value={data.laufzeit_bis_text} testId="ops-vertrag-laufzeit-bis" />
                  <Zeile label="Letzter Kündigungstag" value={data.letzter_kuendigungstag_text} testId="ops-vertrag-kuendigungstag" />
                  <Zeile
                    label="Kündigungsstatus"
                    value={k ? `Gekündigt zum ${k.wirksam_zum_text} (${INITIATOR_LABEL[k.initiator] || k.initiator}, Eingang ${k.eingang_am_text})` : "keine Kündigung"}
                    testId="ops-vertrag-kuendigungsstatus"
                  />
                </>
              )}
              {data.stripe_cancel_at_text && (
                <Zeile label="Abo-Ende in Stripe" value={data.stripe_cancel_at_text} testId="ops-vertrag-stripe-cancel-at" />
              )}
            </div>
            {ve && <VertragsendeBlock ve={ve} />}
            {!data.vertragsbeginn && !ve && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="ops-vertrag-beginn-unbekannt">
                Vertragsbeginn unbekannt – Laufzeit und Kündigung sind erst nach dem nächsten Abgleich mit Stripe (Owner-Login oder Tarifseite) verfügbar.
              </p>
            )}
            {(data.hinweise || []).map((h) => (
              <p key={h} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="ops-vertrag-hinweis">
                {h}
              </p>
            ))}
            {k?.begruendung && (
              <p className="text-xs text-slate-500" data-testid="ops-vertrag-begruendung">Begründung: {k.begruendung}</p>
            )}
            {meldung && (
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="ops-vertrag-meldung">
                {meldung}
              </p>
            )}
            {ve ? null : k ? (
              <Ruecknahme tenantId={tenantId} onDone={() => nachAktion("Die Kündigung wurde zurückgenommen.")} />
            ) : data.vertragsbeginn ? (
              <KuendigungFormular
                tenantId={tenantId}
                onDone={(res) => nachAktion(`Kündigung eingetragen – wirksam zum ${res?.wirksam_zum_text || "–"}.`)}
              />
            ) : null}
            <VertragHistorie eintraege={data.vertrag_historie} />
            {(data.historie || []).filter((h) => h.status === "zurueckgenommen").length > 0 && (
              <div className="text-xs text-slate-500 space-y-0.5" data-testid="ops-vertrag-historie">
                <p className="font-semibold uppercase tracking-wide">Zurückgenommene Kündigungen</p>
                {data.historie
                  .filter((h) => h.status === "zurueckgenommen")
                  .map((h) => (
                    <p key={h.kuendigung_id}>
                      Eingang {h.eingang_am_text}, war wirksam zum {h.wirksam_zum_text}, zurückgenommen – {h.ruecknahme_begruendung}
                    </p>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
